import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Chip, Modal, SkeletonLoader, Text, Truncated } from '@frontend/design-system';
import { buildAudioLibraryPath } from '../../../utils/media-path';
import { getNameWithoutExtension } from '../../../utils/phone-utils';
import { AudioQueryMedia } from '../../../utils/use-audio-query';
import { usePhoneTreeData } from '../../../views/settings/use-phone-tree-data';
import { findMediaItem } from '../../audio-picker/audio-picker';
import { CachedAudioScrubber } from '../../common/cached-audio-scrubber';
import { DialOptions } from '../../phone-tree-quick-actions/phone-tree-card';
import { DialOptionData } from '../../phone-tree-quick-actions/types';

export const PhoneTreePreviewModal = ({
  phoneTreeId,
  name,
  onClose,
  audioFiles,
  precomputedData,
  callRouteName,
  callRouteLocationId,
}: {
  phoneTreeId?: string;
  name: string;
  onClose: () => void;
  audioFiles: AudioQueryMedia;
  precomputedData?: {
    dialOptions: DialOptionData[];
    extensionDialingEnabled?: boolean;
    promptMediaItemId: string;
  };
  callRouteName?: string;
  callRouteLocationId?: string;
}) => {
  const { t } = useTranslation('phone');

  // Only fetch data if we don't have precomputed data
  const { phoneTreeData, phoneTreeIsLoading, transformedOptions } = usePhoneTreeData({
    phoneTreeId: phoneTreeId ?? '',
    callObjectsQueryOptions: { staleTime: 1 * 60 * 1000 },
  });

  // Use precomputed data if available, otherwise use fetched data
  const dialOptions = precomputedData?.dialOptions ?? transformedOptions;
  const extensionDialingEnabled = precomputedData?.extensionDialingEnabled ?? phoneTreeData?.extensionDialing;
  const promptMediaItemId = precomputedData?.promptMediaItemId ?? phoneTreeData?.promptMediaItemId;
  const isLoading = !precomputedData && phoneTreeIsLoading;
  const hasData = precomputedData || phoneTreeData;

  return (
    <>
      <Modal.Header onClose={onClose}>
        {t('Preview {{phoneTreeName}}', { phoneTreeName: name })}
        <Text weight='regular'>
          {hasData
            ? t('View dial numbers* and see where calls go when someone selects that number.')
            : t('View dial numbers and see where calls go when someone selects that number.')}
        </Text>
      </Modal.Header>
      <Modal.Body css={modalBodyStyle}>
        <CallRouteDetailsHeader callRouteName={callRouteName} locationId={callRouteLocationId} />
        {isLoading ? (
          <div css={loadingContainerStyle}>
            <SkeletonLoader height={72} />
            <SkeletonLoader height={72} />
            <SkeletonLoader height={72} />
          </div>
        ) : hasData ? (
          <>
            <PhoneTreePromptSection mediaItemId={promptMediaItemId ?? ''} audioFiles={audioFiles} />
            <Text weight='bold'>{t('Dial Options')}</Text>
            <DialOptions options={dialOptions} isReadOnly={true} disableEditing={true} />
          </>
        ) : (
          <Text weight='bold'>{t('No data available.')}</Text>
        )}
      </Modal.Body>
      {hasData && (
        <Modal.Footer>
          <div>
            <Text weight='bold'>
              {extensionDialingEnabled ? t('Dial By Extension Enabled') : t('Dial By Extension Disabled')}
            </Text>
            <Text>
              {extensionDialingEnabled
                ? t('* Callers in this Phone Tree can dial office device extensions. ')
                : t("* Callers in this Phone Tree can't dial office device extensions. ")}
            </Text>
          </div>
        </Modal.Footer>
      )}
    </>
  );
};

// Component that shows the call route name and the location chip displaying the call route location name (if unify.
// Used in the phone tree preview modal.
const CallRouteDetailsHeader = ({ callRouteName, locationId }: { callRouteName?: string; locationId?: string }) => {
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const isUnify = settingsTenantLocation?.locationType === 'unify';
  const { getScopeName } = useAppScopeStore();

  if (!callRouteName && !locationId) {
    return null;
  }

  return (
    <div css={callRouteHeaderOuterStyle}>
      {callRouteName && (
        <div css={callRouteHeaderInnerStyle}>
          <Icon name='call-route' color='subdued' css={iconNoShrinkStyle} />
          <Text color='subdued' weight='regular'>
            {callRouteName}
          </Text>
        </div>
      )}
      {isUnify && locationId && <Chip.SingleChip>{getScopeName(locationId ?? '')}</Chip.SingleChip>}
    </div>
  );
};

const PhoneTreePromptSection = ({ mediaItemId, audioFiles }: { mediaItemId: string; audioFiles: AudioQueryMedia }) => {
  const { t } = useTranslation('phone');

  if (!mediaItemId) {
    return null;
  }

  let filePath = '';
  const media = findMediaItem(mediaItemId, audioFiles?.allMedia || []);
  if (media) {
    filePath = buildAudioLibraryPath({ media });
  }

  return (
    <div css={promptSectionContainerStyle}>
      <Text weight='bold'>{t('Prompt')}</Text>
      <Truncated as={Text} size='small' color='subdued'>
        {getNameWithoutExtension(media?.name)}
      </Truncated>
      <CachedAudioScrubber singlePlayer mediaId={mediaItemId} filePath={filePath} />
    </div>
  );
};

const modalBodyStyle = css`
  max-height: 600px;
  overflow: auto;
`;

const loadingContainerStyle = css`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: ${theme.spacing(2)};
`;

const callRouteHeaderOuterStyle = css`
  display: flex;
  gap: ${theme.spacing(3)};
`;

const callRouteHeaderInnerStyle = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
  max-width: 78%;
`;

const iconNoShrinkStyle = css`
  flex-shrink: 0;
`;

const promptSectionContainerStyle = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(0.5)};
  max-width: 240px;
  position: relative;
`;
